import { PropsWithChildren, useEffect, useState } from "react";
import { darkTheme } from "../theme/ThemeDark.css";
import { lightTheme } from "../theme/ThemeLight.css";
import { Theme, ThemeProviderContext } from "./ThemeContext";

interface ThemeProviderProps {
  /**
   * Optional classes to apply for dark and light themes,
   * defaults to darkTheme and lightTheme from the ui-kit
   */
  themes?: {
    dark: string;
    light: string;
  };
  /**
   * The default theme to use when the component mounts,
   * if no existing preference is found. Defaults to system theme.
   */
  defaultTheme?: Theme;
  /**
   * Optional key used to store the theme preference in localStorage.
   * Defaults to "ui-theme".
   */
  storageKey?: string;
}

export function ThemeProvider({
  children,
  themes = {
    dark: darkTheme,
    light: lightTheme
  },
  defaultTheme = checkPreferredTheme(),
  storageKey = "ui-theme"
}: PropsWithChildren<ThemeProviderProps>) {
  const [theme, setTheme] = useState<Theme>(
    () => (localStorage.getItem(storageKey) as Theme) || defaultTheme
  );

  useEffect(() => {
    applyThemeClass(themes, theme);
  }, [theme, themes]);

  const value = {
    theme,
    setTheme: (theme: Theme) => {
      localStorage.setItem(storageKey, theme);
      // Before the re-render, not in the effect after it: a child that
      // resolves a CSS var while rendering must read the new theme's value.
      applyThemeClass(themes, theme);
      setTheme(theme);
    }
  };

  return (
    <ThemeProviderContext.Provider value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

function applyThemeClass(
  themes: Required<ThemeProviderProps>["themes"],
  theme: Theme
) {
  const root = window.document.documentElement;
  root.classList.remove(themes.dark, themes.light);
  root.classList.add(themes[theme]);
}

function checkPreferredTheme(): Theme {
  // Developer note: Preferred system detection isn't necessary at the moment
  // return window.matchMedia("(prefers-color-scheme: dark)").matches
  //   ? "dark"
  //   : "light";

  return "dark";
}
